<template>
    <b-container fluid>
        <b-row class="mb-4">
            <b-col md="3" sm="6" class="mb-2 mb-md-0">
                <div @click="OnSearchEvent" 
                     class="border rounded h-100 d-flex flex-column align-items-center justify-content-center p-3 text-center" 
                     :class="[isSearchMeterActive ? 'bg-white shadow-sm border-' + Variant + ' text-' + Variant : 'bg-light text-muted']"
                     style="cursor: pointer; transition: all 0.2s;">
                    <div class="mb-2" style="font-size: 1.5rem;">
                        <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
                    </div>
                    <div style="font-size: 0.9rem; font-weight: 500;">
                        {{ $t('OpenSearchEvent') }}
                    </div>
                </div>
            </b-col>

            <b-col v-if="!meterSearchOnly" md="3" sm="6" class="mb-2 mb-md-0">
                <div @click="OnSearchLOEvent" 
                     class="border rounded h-100 d-flex flex-column align-items-center justify-content-center p-3 text-center" 
                     :class="[isSearchLocationActive ? 'bg-white shadow-sm border-' + Variant + ' text-' + Variant : 'bg-light text-muted']"
                     style="cursor: pointer; transition: all 0.2s;">
                    <div class="mb-2" style="font-size: 1.5rem;">
                        <font-awesome-icon icon="fa-solid fa-thumbtack" />
                    </div>
                    <div style="font-size: 0.9rem; font-weight: 500;">
                        {{ $t('OpenSearchLBOEvent') }}
                    </div>
                </div>
            </b-col>
            <b-col v-if="!meterSearchOnly" md="3" sm="6" class="mb-2 mb-md-0">
                <div @click="OnImportRemainMeters" 
                     class="border rounded h-100 d-flex flex-column align-items-center justify-content-center p-3 text-center bg-light text-muted"
                     style="cursor: pointer; transition: all 0.2s;">
                    <div class="mb-2" style="font-size: 1.5rem;">
                        <font-awesome-icon icon="fa-solid fa-file-import" />
                    </div>
                    <div style="font-size: 0.9rem; font-weight: 500;">
                        {{ $t('ImportMetersWithoutCertificate') }}
                    </div>
                </div>
            </b-col>
            <b-col v-if="!meterSearchOnly" md="3" sm="6" class="mb-2 mb-md-0">
                <div @click="displayImport = true" 
                     class="border rounded h-100 d-flex flex-column align-items-center justify-content-center p-3 text-center" 
                     :class="[displayImport ? 'bg-white shadow-sm border-' + Variant + ' text-' + Variant : 'bg-light text-muted']"
                     style="cursor: pointer; transition: all 0.2s;">
                    <div class="mb-2" style="font-size: 1.5rem;">
                        <font-awesome-icon icon="fa-solid fa-folder-open" />
                    </div>
                    <div style="font-size: 0.9rem; font-weight: 500;">
                        {{ $t('OpenImportEvent') }}
                    </div>
                </div>
            </b-col>
        </b-row>
        <MetersList 
            @RefreshEvent="OnRefresh"
            @DeleteEvent="OnDelete"
            :Certificate="certificate"
            :Mode="Mode" >
            <template #toolbar-right>
                <b-button 
                    @click="OnDeleteSelectedMeters" 
                    variant="outline-light" 
                    class="btn-circle border-0 mx-1" 
                    pill 
                    v-b-tooltip.hover 
                    :title="$t('DeleteSelectedMeters')">
                    <font-awesome-icon icon="fa-solid fa-eraser" />
                </b-button>
                <b-button 
                    @click="OnDeleteOnErrorMeters" 
                    variant="outline-light" 
                    class="btn-circle border-0 mx-1" 
                    pill 
                    v-b-tooltip.hover 
                    :title="$t('DeleteOnErrorMeters')">
                    <font-awesome-icon icon="fa-solid fa-trash" />
                </b-button>
            </template>
        </MetersList>
        <b-col class="d-flex justify-content-end align-items-center h-100 mt-2">
            <h6>
                <b-badge>
                    {{ certificate.MeterInformations.Meters.length }} {{ $t('AddedMeters') }}
                </b-badge>
            </h6>
        </b-col>
        <RAEImport 
            @OnValidation="AddImport" 
            :Certificate="certificate"
            :Show.sync="displayImport" 
            :Types="Form.MetersTypesData.flatMap(meterType => meterType.Type)" />
        <Search 
            @ShortMeterSelectedEvent="onShortMeterSelectedEvent"
            @NodeSelectedEvent="onLOSelectedEvent"
            :Expendables="expendables" 
            :TypesList="typesList" 
            :SearchStrategy="strategy" 
            :Visible.sync="visible" />
    </b-container>
</template>

<script lang="ts">
    /**
     * Composants Vue js
     */
    import { Component, InjectReactive, Prop, PropSync, Vue } from 'vue-property-decorator';
    import { mixins } from 'vue-class-component';

    /**
     * Composants
     */
    import MetersList from './DistributorForm/MetersList.vue';
    import Search from '@/components/commons/Search.vue'; 
    import RAEImport from './DistributorForm/ClientForm/RAEImport.vue';
    import { SMAUtils } from './SMAUtils';

    /**
     * Traductions
     */
    import locale from '../locales/SMA.json';

    /**
     * Classes et énumérations
     */
    import { Information, Meter } from '@/types/Administration/SMA/MetersInformations';
    import { SMADistributorForm } from '@/types/Administration/SMA/SMADistributorForm';
    import BaseShortMeter from '@/types/BaseShortMeter';
    import BaseShortLBO from '@/types/BaseShortLBO';
    import SearchStrategyEnum from '@/enums/commons/SearchStrategyEnum';
    import { SMACertificate } from '@/types/Administration/SMA/SMACertificate';

    @Component({
        components: {
            MetersList,
            Search,
            RAEImport
        },
        i18n: {messages: locale}
    })
    /**
     * Formulaire des compteurs
     */
    export default class MetersForm extends mixins(Vue, SMAUtils) {

        /**
         * Mandat distributeur
         */
        @PropSync('Certificate', { default: () => undefined })
        public certificate!: SMACertificate;

        /**
         * Formulaire distributeur
         */
        @Prop()
        public Form!: SMADistributorForm;

        /**
         * Sélection des compteurs limitée à la recherche par compteur
         * Description du formulaire portée par le serveur (SMAAttribute MeterSearchOnly)
         */
        private get meterSearchOnly() : boolean
        {
            return this.Form?.MeterSearchOnly ?? false;
        }

        /** 
         * Variant de couleur à appliquer sur le composant 
         */
        @InjectReactive()
        public Variant!: string;

        /**
         * Mode d'utilisation du composant (Edit ou View)
         */
        private Mode: string = "Edit";

        /**
         * Affichage de la fenêtre d'import
         */
        private displayImport: boolean = false;

        /**
         * Stratégie de recherche de noeud
         */
        private strategy: SearchStrategyEnum = SearchStrategyEnum.IShortMeterSearch;

        /**
         * Affichage du composant de recherche de noeud
         */
        private visible: boolean = false;
        
        /**
         * Liste des types d'objets à rechercher dans le composant de recherche de noeud
         */
        private typesList: string[] = [];

        /**
         * Liste des énergies à rechercher dans le composant de recherche de noeud
         */
        private expendables: string[] = [];

        /**
         * Indique si l'onglet/carte "Rechercher des compteurs" est actif
         */
        public get isSearchMeterActive(): boolean {
            return this.visible && this.strategy === SearchStrategyEnum.IShortMeterSearch;
        }

        /**
         * Indique si l'onglet/carte "Rechercher par localisation" est actif
         */
        public get isSearchLocationActive(): boolean {
            return this.visible && this.strategy === SearchStrategyEnum.INodeSearch;
        }

        /**
         * Event de selection du noeud dans la composant de recherche (Search)
         * @param item Noeud sélectionné
         */
        private onShortMeterSelectedEvent(item: BaseShortMeter): void 
        {
            const info = new Information({
                PersistentRef: item.Id,
                MeterName: item.Name,
                Invariant: item.Invariant,
                SupplyPointNumber: item.SupplyPointNumber,
                Address: item.Address,
                Expendables: item.Expendables,
            });

            // Conversion d'un type BaseShortMeter en Meter
            const meter = new Meter(
            {
                RaepceNumber: item.RaepceNumber, 
                Informations: info,
                Services: [],
                Errors: []
            });

            this.$store.dispatch('CertificateStore/CheckThisMeter', { item: this.certificate, meter: meter }).then((result) => {
                // Ajout sur la liste des compteurs le compteur sélectionné dans le composant Search
                
                if (result.success && result.data)
                    this.AddToMetersList(result.data);

                this.showToast(result.success ? this.$t("MetersImportComplete").toString(): this.$t("MetersImportFailed").toString(), result.success ? "success" : "danger", result.message);
            }).catch((error) => {
                this.showToast(this.$t("ValidateMeterFailed").toString(), "danger", "Erreur lors de la validation du compteur.");
            });
        }

        /**
         * Lors de la selection d'un noeud de type LBO dans le composant de recherche de noeud (Search), récupération du compteur lié à ce noeud et ajout à la liste des compteurs
         * @param item Noeud sélectionné
         */
        private onLOSelectedEvent(item: BaseShortLBO): void 
        {
            this.$store.dispatch('CertificateStore/GetMeterByLO', 
            { item: this.certificate, LoId: item.Id, Types: this.Form.MetersTypesData.map(meterType => meterType.Type) }).then((result) => 
            {
                if (result.success && result.data)
                    this.AddToMetersList(result.data);

                this.showToast(result.success ? this.$t("MetersImportComplete").toString(): this.$t("MetersImportFailed").toString(), result.success ? "success" : "danger", result.message);
            }).catch((error) => {
                console.error("Error in GetMeterByLO:", error);
                this.showToast("Erreur lors de la récupération du compteur via LBO.", "danger", "GetMeterByLOFailed");
            });
        }

        /**
         * Affichage du composant de recherche de compteur
         */
        private OnSearchEvent(): void
        {
            this.strategy = SearchStrategyEnum.IShortMeterSearch;
            this.typesList = this.Form.MetersTypesData.map(meterType => meterType.Type);
            this.expendables = this.Form.Expendables;
            this.displayImport = false;
            this.visible = true; 
        }

        /**
         * Affichage du composant de recherche de LO
         * Mise en place de la stratégie de recherche en mode INodeSearch
         * Ajout dans la type liste des types d'objets à rechercher les types liés aux LO
         * Affichage du composant Search
         */
        private OnSearchLOEvent(): void
        {
            this.strategy = SearchStrategyEnum.INodeSearch;
            this.typesList = this.Form.LO;
            this.displayImport = false;
            this.visible = true; 
        }

        /**
         * Importation des compteurs via fichier excel
         * @param importedMeters Liste des compteurs importés
         */
        private AddImport(importedMeters: Array<Meter>) : void
        {
            this.displayImport = false;
            this.AddToMetersList(importedMeters);
        }

        /**
         * Suppression des tous les compteurs de la liste
         */
        private OnDeleteSelectedMeters() : void
        {
            this.certificate.MeterInformations.Meters = this.certificate.MeterInformations.Meters.filter((meter) => !meter.Selected);
        }

        /**
         * Suppression des compteurs en erreur de la liste
         */
        private OnDeleteOnErrorMeters() : void
        {
            this.certificate.MeterInformations.Meters  = this.certificate.MeterInformations.Meters .filter((meter) => meter.Errors.length == 0);
        }

        /**
         * Importation de l'ensemble des compteurs restants sans mandat
         */
        private OnImportRemainMeters() : void
        {
            this.$store.dispatch('CertificateStore/ImportRemainMeters', 
            { item: this.certificate, types: this.Form.MetersTypesData.map(meterType => meterType.Type) }).then((result) => 
            {
                if (result.success)
                    this.AddToMetersList(result.data);

                this.showToast(result.success ? this.$t("MetersImportComplete").toString(): this.$t("MetersImportFailed").toString(), result.success ? "success" : "danger", result.message);
            });
        }

        /**
         * Vérifie si compteur déjà existant dans la liste, Si non présent l'ajoute à la liste
         * @param importedMeters liste des compteurs à ajouter
         */
        private AddToMetersList(importedMeters: Array<Meter>) : void
        {
            const idsExistants = new Set(this.certificate.MeterInformations.Meters .map(item => item.Informations.PersistentRef));
            const nouveauxItems = importedMeters.filter(item => !idsExistants.has(item.Informations.PersistentRef));

            this.certificate.MeterInformations.Meters  = [...this.certificate.MeterInformations.Meters , ...nouveauxItems];
        }

        /**
         * Rafraichissement des données du compteur
         * Utilisé si une information du compteur est incorrect (Exemple : RAE, Adresse, Energie)
         * Ce méthode va mettre à jour les informations du compteur suivant les données de la fiche objet
         * @param Id Identifiant du compteur
         */
        private OnRefresh(Id: string) : void
        {
            this.$emit('OnRefreshMeter', Id);
        }

        /**
         * Suppression du compteur
         * @param nodeId Identifiant du compteur à supprimer
         */
        private OnDelete(nodeId: string) : void
        {
            this.certificate.MeterInformations.Meters  = this.certificate.MeterInformations.Meters .filter((n) => n.Informations.PersistentRef !== nodeId);
        }
    }
</script>